/**
 * orders-attention-model — the shared "action-first" taxonomy for the seller
 * Orders experience (desktop + mobile). Everything here is a pure function of
 * OrderStatus, so server-truth counts (from per-status facets) and the rendered
 * row lists stay in agreement.
 *
 * Sections:
 *   - attention : the seller has a next action; grouped by that action (reason),
 *                 urgency-ordered (most time-critical first).
 *   - waiting   : waiting on the buyer — nothing for the seller to do.
 *   - completed : delivered / cancelled / declined / draft.
 */

import type { OrderStatus, SellerOrderListItem } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import { getStatusMeta } from "./order-status-meta";

export type AttentionSection = "attention" | "waiting" | "completed";

/** Seller-action statuses, most time-sensitive first. Index = urgency rank:
 *  verify money first, then drive fulfilment (confirm → prepare → ship → deliver). */
export const ATTENTION_ORDER: OrderStatus[] = [
  "pending_payment_verification",
  "paid_less_than_required",
  "pending_seller_confirmation",
  "confirmed",
  "preparing",
  "shipped",
];

/** Buyer-waiting statuses — no seller action, so their own quiet section. */
export const WAITING_ON_BUYER: OrderStatus[] = [
  "pending_buyer_verification",
  "pending_buyer_payment",
];

/** True when the order is waiting on a seller action ("Needs attention"). */
export function isAttention(status: OrderStatus): boolean {
  return ATTENTION_ORDER.includes(status);
}

/** True when the order is waiting on the *buyer* — nothing for the seller to do. */
export function isWaitingOnBuyer(status: OrderStatus): boolean {
  return WAITING_ON_BUYER.includes(status);
}

/** Lower = more time-sensitive. Non-action statuses sort last. */
export function attentionRank(status: OrderStatus): number {
  const i = ATTENTION_ORDER.indexOf(status);
  return i < 0 ? 999 : i;
}

/** Which section an order belongs to. */
export function sectionForStatus(status: OrderStatus): AttentionSection {
  if (isAttention(status)) return "attention";
  if (isWaitingOnBuyer(status)) return "waiting";
  return "completed";
}

/** A next-action "reason" — the unit the attention section groups by. */
export type ReasonDef = {
  /** Stable id (the next-action label key). */
  key: string;
  /** i18n key for the group header. */
  labelKey: string;
  /** Statuses that share this next action. */
  statuses: OrderStatus[];
  /** Min urgency rank across its statuses (drives group order). */
  rank: number;
  /** Representative status — drives the ring colour. */
  sampleStatus: OrderStatus;
};

/** Canonical attention reasons, derived once from ATTENTION_ORDER by grouping
 *  statuses that share a next action, ordered by urgency. */
export const ATTENTION_REASONS: ReasonDef[] = buildReasonDefs();

function buildReasonDefs(): ReasonDef[] {
  const byKey = new Map<string, ReasonDef>();
  ATTENTION_ORDER.forEach((status, i) => {
    const labelKey = getStatusMeta(status).nextLabelKey;
    const existing = byKey.get(labelKey);
    if (existing) {
      existing.statuses.push(status);
      existing.rank = Math.min(existing.rank, i);
    } else {
      byKey.set(labelKey, { key: labelKey, labelKey, statuses: [status], rank: i, sampleStatus: status });
    }
  });
  return [...byKey.values()].sort((a, b) => a.rank - b.rank);
}

/** A reason group with attached order rows, for rendering a section list. */
export type ReasonGroup = ReasonDef & { orders: SellerOrderListItem[] };

/** Splits orders into attention reason groups (urgency-ordered, oldest-first).
 *  Non-attention orders in the input are ignored. */
export function buildReasonGroups(orders: SellerOrderListItem[]): ReasonGroup[] {
  return ATTENTION_REASONS.map((def) => ({
    ...def,
    orders: orders
      .filter((o) => def.statuses.includes(o.status))
      .slice()
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
  })).filter((g) => g.orders.length > 0);
}

/** A per-status count map (as returned by the status-counts facet). */
export type StatusCountMap = Partial<Record<OrderStatus, number>>;

const sumStatuses = (counts: StatusCountMap, statuses: OrderStatus[]): number =>
  statuses.reduce((n, s) => n + (counts[s] ?? 0), 0);

/** Server-truth reason-group counts (urgency-ordered), from a per-status map. */
export function reasonGroupCounts(counts: StatusCountMap): Array<ReasonDef & { count: number }> {
  return ATTENTION_REASONS.map((def) => ({
    ...def,
    count: sumStatuses(counts, def.statuses),
  })).filter((g) => g.count > 0);
}

/** Total orders needing a seller action, from a per-status map. */
export function attentionCount(counts: StatusCountMap): number {
  return sumStatuses(counts, ATTENTION_ORDER);
}

/** Total orders waiting on the buyer, from a per-status map. */
export function waitingOnBuyerCount(counts: StatusCountMap): number {
  return sumStatuses(counts, WAITING_ON_BUYER);
}

/** Statuses surfaced in the always-on top cards on desktop — the needs_attention
 *  bucket (Review payment + Confirm) plus Waiting on buyer. Excluded from the
 *  browse table/cards so those orders aren't shown twice. */
export const TOP_CARD_STATUSES: OrderStatus[] = [
  "pending_payment_verification",
  "paid_less_than_required",
  "pending_seller_confirmation",
  "pending_buyer_verification",
  "pending_buyer_payment",
];

export function isInTopCards(status: OrderStatus): boolean {
  return TOP_CARD_STATUSES.includes(status);
}

/** A rendered list section: header (label + colour) + its order rows. */
export type ListSection = {
  key: string;
  labelKey: string;
  /** Representative status for the header colour, or null. */
  sampleStatus: OrderStatus | null;
  orders: SellerOrderListItem[];
};

/** Completed outcomes, in display order — labelled by outcome, not next action
 *  (they all share the "View order" next step, so a reason split is useless). */
const COMPLETED_GROUPS: Array<{
  key: string;
  labelKey: string;
  match: (s: OrderStatus) => boolean;
}> = [
  { key: "delivered", labelKey: keys.orders.groupDelivered, match: (s) => s === "delivered" },
  {
    key: "closed",
    labelKey: keys.orders.groupClosed,
    match: (s) => s === "cancelled" || s === "declined",
  },
  { key: "drafts", labelKey: keys.orders.groupDrafts, match: (s) => s === "draft" },
];

/** Groups a list into action-first sections: attention reason groups
 *  (urgency-ordered) → Waiting on buyer → completed (delivered/closed/drafts).
 *  Row order within each section is preserved (respects the caller's sort).
 *  Used to render the reason sub-sections inside the desktop table & cards. */
export function groupOrdersForList(orders: SellerOrderListItem[]): ListSection[] {
  const sections: ListSection[] = [];
  for (const def of ATTENTION_REASONS) {
    const rows = orders.filter((o) => def.statuses.includes(o.status));
    if (rows.length) {
      sections.push({
        key: `attn_${def.key}`,
        labelKey: def.labelKey,
        sampleStatus: def.sampleStatus,
        orders: rows,
      });
    }
  }
  const waiting = orders.filter((o) => isWaitingOnBuyer(o.status));
  if (waiting.length) {
    sections.push({
      key: "waiting",
      labelKey: keys.orders.waitingOnBuyerSectionLabel,
      sampleStatus: waiting[0].status,
      orders: waiting,
    });
  }
  for (const c of COMPLETED_GROUPS) {
    const rows = orders.filter((o) => c.match(o.status));
    if (rows.length) {
      sections.push({
        key: c.key,
        labelKey: c.labelKey,
        sampleStatus: rows[0].status,
        orders: rows,
      });
    }
  }
  return sections;
}
